<script lang="ts">
	import "@glidejs/glide/dist/css/glide.core.min.css";
	import * as Glide from "@glidejs/glide";
	import logo from "../../../../core/assets/logo.svg";
	import type { ActionReturn } from "svelte/action";
	import type { UploadedFile } from "../../../../core/schema/UploadedFile.js";
	import type { Reference } from "../../../../core/schema/Reference.js";
	import { disableGlidePerSlideAmount } from "../../utils/disableGlidePerSlideAmount.js";
	import DynamicPicture from "../Picture/DynamicPicture.svelte";
	import StaticPicture from "../Picture/StaticPicture.svelte";
	import { appName } from "../../../../core/schema/names/appName.js";
	import translations from "./ReferenceGallery.translations.json";
	import { getSharedTranslate } from "../../utils/getSharedTranslate.js";

	export let images: UploadedFile[];
	export let reference: Reference;
	export let gliderClass = "";
	export { gliderClass as class };

	function glide(element: HTMLDivElement): ActionReturn {
		const glideOptions = {
			type: "carousel",
			startAt: 0,
			focusAt: "center",
			perView: 2.75,
			autoplay: 3000,
			hoverpause: false,
			animationDuration: 3000,
			animationTimingFunc: "linear",
			gap: 9,
			keyboard: true,
			cloningRatio: 2,
			breakpoints: {
				736: { perView: 2.7 },
				720: { perView: 2.6 },
				670: { perView: 2.35 },
				590: { perView: 2.2 },
				570: { perView: 2.1 },
				540: { perView: 2 },
				515: { perView: 1.85 },
				480: { perView: 1.75 },
				455: { perView: 1.65 },
				420: { perView: 1.5 },
				370: { perView: 1.35 },
				340: { perView: 1.225 },
			},
		} satisfies Partial<Glide.Options>;

		// eslint-disable-next-line new-cap
		const glide = new Glide.default(element, glideOptions).mount();
		glide.on("resize", () => {
			disableGlidePerSlideAmount(glide, element);
		});

		return {
			destroy(): void {
				glide.destroy();
			},
		};
	}

	const translate = getSharedTranslate(translations);
</script>

{#if images.length > 0}
	<div class={gliderClass}>
		<div use:glide class="glide">
			<div class="glide__track" data-glide-el="track">
				<div class="glide__slides">
					{#each images as image, i}
						<DynamicPicture
							class="glide__slide aspect-square !h-[22rem] rounded-lg bg-white"
							imgClass="max-w-full h-full w-auto rounded-lg aspect-square object-cover"
							{image}
							lightboxId={reference.title}
							lightboxWidth={400}
							width={273}
							height={352}
							bucket="storage"
							loading="eager"
							alt="{translate('photo')} – {reference.title} ({i + 1}/{images.length})"
						/>
					{/each}
				</div>
				<div class="flex justify-center">
					<div class="glide__bullets bg-secondaryBlack-150 mt-5 flex h-[2px] w-10/12" data-glide-el="controls[nav]">
						{#each images as _image, i}
							<span
								class="glide__bullet text-secondaryBlack hover:bg-secondaryBlack-300 w-full cursor-pointer transition-all duration-200"
								data-glide-dir="={i}"
							></span>
						{/each}
					</div>
				</div>
			</div>
		</div>
	</div>
{:else}
	<div class="flex justify-center">
		<div
			class="group flex aspect-square h-[17rem] w-[90%] items-center justify-center rounded-lg bg-white p-4 md:hidden"
		>
			<StaticPicture
				alt="Logo {appName}"
				image={logo}
				class="w-24 opacity-50 transition-all duration-300 group-hover:opacity-100"
				loading="eager"
				width={96}
				height={96}
			/>
		</div>
	</div>
{/if}
